import React, { useState, useEffect } from "react";
import { Modal, Button, message, Form } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import FloatInput from "../../util/FloatInput";
import "./ResetPasswordModal.css";
import { useAuth } from "../../authentication";
import { httpClient } from "../../util/Api";

const encryptPassword = (password) => {
  const key = "SSA-SOFT-2024";
  let encrypted = "";

  for (let i = 0; i < password.length; i++) {
    encrypted += String.fromCharCode(
      password.charCodeAt(i) ^ key.charCodeAt(i % key.length),
    );
  }

  return btoa(encrypted);
};

const ResetPasswordModal = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState(false);
  const { authUser, userSignOut, getLogout } = useAuth();
  const history = useHistory();

  const user = JSON.parse(localStorage.getItem("user")) || authUser?.User;

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setLoading(false);
      setPasswordsMatch(false);
      setNewPassword("");
      setConfirmPassword("");
      setCurrentPasswordError(false);
    }
  }, [visible, form]);

  useEffect(() => {
    if (newPassword && confirmPassword) {
      setPasswordsMatch(
        newPassword === confirmPassword && newPassword.length >= 8,
      );
    } else {
      setPasswordsMatch(false);
    }
  }, [newPassword, confirmPassword]);

  const handleNewPasswordChange = (value) => {
    setNewPassword(value);
    form.setFieldsValue({ newPassword: value });
  };

  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    form.setFieldsValue({ confirmPassword: value });
  };

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject(new Error("Please input password"));
    }
    const currentPassword = form.getFieldValue("currentPassword");
    if (currentPassword && value === currentPassword) {
      return Promise.reject(
        new Error("New password cannot be same as current password"),
      );
    }

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    const hasMinLength = value.length >= 8;

    if (!hasMinLength) {
      return Promise.reject(
        new Error("Password must be at least 8 characters"),
      );
    }
    if (!hasUpperCase) {
      return Promise.reject(
        new Error("Password must contain at least 1 capital letter"),
      );
    }
    if (!hasLowerCase) {
      return Promise.reject(
        new Error("Password must contain at least 1 lowercase letter"),
      );
    }
    if (!hasNumber) {
      return Promise.reject(
        new Error("Password must contain at least 1 number"),
      );
    }
    if (!hasSpecialChar) {
      return Promise.reject(
        new Error("Password must contain at least 1 special character"),
      );
    }

    return Promise.resolve();
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: "", color: "" };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength < 3) return { level: 1, text: "Weak", color: "#ff4d4f" };
    if (strength < 5) return { level: 2, text: "Medium", color: "#faad14" };
    return { level: 3, text: "Strong", color: "#52c41a" };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const performLogout = () => {
    if (getLogout) {
      getLogout();
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("authUser");

      if (userSignOut) {
        userSignOut(() => {
          history.push("/signin");
        });
      } else {
        history.push("/signin");
      }
    }
  };

  const onFinish = async (values) => {
    if (!user) {
      message.error("User not logged in. Please login again.", 3);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        currentPassword: encryptPassword(values.currentPassword),
        newPassword: encryptPassword(values.newPassword),
      };

      const response = await httpClient.post("Login/UpdatePassword", payload);

      let responseMsg = "";

      if (typeof response.data === "string") {
        responseMsg = response.data;
      } else if (response.data && response.data.msg) {
        responseMsg = response.data.msg;
      } else if (response.data && response.data.message) {
        responseMsg = response.data.message;
      } else {
        responseMsg = "Password updated successfully!";
      }

      const isError =
        responseMsg.toLowerCase().includes("error") ||
        responseMsg.toLowerCase().includes("failed") ||
        responseMsg.toLowerCase().includes("incorrect") ||
        responseMsg.toLowerCase().includes("wrong");

      if (isError) {
        setLoading(false);
        if (responseMsg.toLowerCase().includes("current password")) {
          setCurrentPasswordError(true);
        }
        message.error(responseMsg, 3);
      } else {
        setLoading(false);
        form.resetFields();
        onClose();
        message.success(responseMsg, 2);
        setTimeout(() => {
          message.info(
            "Logging out for security. Please login with new password.",
            2,
          );

          setTimeout(() => {
            performLogout();
          }, 1000);
        }, 500);
      }
    } catch (error) {
      setLoading(false);
      const errorMsg =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        "Failed to update password. Please try again.";
      message.error(errorMsg, 3);
    }
  };

  const onFinishFailed = (errorInfo) => {
    setLoading(false);
  };

  const handleCancel = () => {
    setLoading(false);
    form.resetFields();
    onClose();
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      title={
        <div className="reset-password-modal-header">
          <LockOutlined className="reset-password-modal-icon" />
          <span>Change Password</span>
        </div>
      }
      open={visible}
      visible={visible}
      onCancel={handleCancel}
      closable={!loading}
      maskClosable={false}
      keyboard={!loading}
      footer={null}
      className="reset-password-modal"
      centered
      destroyOnClose={true}
      width={520}
      afterClose={() => {
        form.resetFields();
        setLoading(false);
      }}
    >
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        className="reset-password-form"
      >
        <Form.Item
          name="currentPassword"
          rules={[{ required: true, message: "Please input current password" }]}
          validateStatus={currentPasswordError ? "error" : ""}
        >
          <div
            className={`password-field-wrapper ${currentPasswordError ? "field-error" : ""}`}
          >
            <FloatInput
              label="Current Password"
              placeholder="Current password"
              name="currentPassword"
              type="password"
              required
              maxLength={30}
              disabled={loading}
              onChange={(value) => {
                form.setFieldsValue({ currentPassword: value });
                if (currentPasswordError) {
                  setCurrentPasswordError(false);
                }
              }}
            />
          </div>
        </Form.Item>

        <Form.Item name="newPassword" rules={[{ validator: validatePassword }]}>
          <div className="password-field-wrapper">
            <FloatInput
              label="New Password"
              placeholder="New password"
              name="newPassword"
              type="password"
              required
              maxLength={30}
              disabled={loading}
              onChange={(value) => handleNewPasswordChange(value)}
              className="changePassword"
            />
            {newPassword &&
              newPassword !== form.getFieldValue("currentPassword") && (
                <div className="password-strength-indicator">
                  <div className="strength-bar-container">
                    <div
                      className={`strength-bar strength-${passwordStrength.level}`}
                      style={{
                        width: `${(passwordStrength.level / 3) * 100}%`,
                        backgroundColor: passwordStrength.color,
                      }}
                    />
                  </div>
                  <span
                    className="strength-text"
                    style={{ color: passwordStrength.color }}
                  >
                    {passwordStrength.text}
                  </span>
                </div>
              )}
          </div>
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          dependencies={["newPassword"]}
          validateStatus=""
          help=""
          rules={[
            { required: true, message: "" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(""));
              },
            }),
          ]}
        >
          <div className="password-field-wrapper">
            <FloatInput
              label="Confirm Password"
              placeholder="Confirm new password"
              name="confirmPassword"
              type="password"
              required
              maxLength={30}
              disabled={loading}
              onChange={(value) => handleConfirmPasswordChange(value)}
            />
            {confirmPassword && (
              <div className="password-match-indicator">
                {passwordsMatch ? (
                  <span className="match-icon success">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="10" fill="#52c41a" />
                      <path
                        d="M6 10L9 13L14 7"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="match-text">Passwords match</span>
                  </span>
                ) : (
                  confirmPassword.length > 0 && (
                    <span className="match-icon error">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <circle cx="10" cy="10" r="10" fill="#ff4d4f" />
                        <path
                          d="M7 7L13 13M13 7L7 13"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className="match-text">Passwords do not match</span>
                    </span>
                  )
                )}
              </div>
            )}
          </div>
        </Form.Item>

        {/* <div className="password-hint">
          Must contain: 8+ characters, uppercase, lowercase, digit, special
          character
        </div> */}

        <div className="form-actions">
          <Button
            type="default"
            onClick={handleCancel}
            size="large"
            className="cancel-btn"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
            className="submit-btn"
          >
            Change Password
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ResetPasswordModal;
